import { Injectable } from '@angular/core'

import { Services } from '../services'
import { Event, IEvent } from './event'

@Injectable()
export class UtilityService {
    constructor() {
        const navigator = window.navigator
        const userAgent = ((navigator && navigator.userAgent) || '').toLowerCase()

        const ieMatch = userAgent.match(/(?:msie |trident.+?; rv:)(\d+)/)
        const edgeMatch = userAgent.match(/edge\/(\d+)/)

        this.isMSBrowser = ieMatch !== null || edgeMatch !== null
    }

    public isMSBrowser: boolean

    public copyright!: string
    public emptyGuid() {
        return '00000000-0000-0000-0000-000000000000'
    }
    public defaultScenarioGuid() {
        return '11111111-1111-1111-1111-111111111111'
    }
    public systemGuid() {
        return '11111111-1111-1111-1111-111111111111'
    }

    private _layoutRequiresUpdate: Event = new Event()
    public get layoutRequiresUpdate(): IEvent {
        return this._layoutRequiresUpdate
    }

    public raiseLayoutRequiresUpdate() {
        this._layoutRequiresUpdate.raise()
    }

    private _userProfileChanged: Event = new Event()
    public get userProfileChanged(): IEvent {
        return this._userProfileChanged
    }

    public raiseUserProfileChanged() {
        this._userProfileChanged.raise()
    }

    /**
     * Converts the specified object to an instance of the specified type.
     * @param source The source object to convert.
     * @param type The type (class) to convert the source object to.
     * @param constructorParams Any parameters to pass when constructing the new instance.
     */
    public deserializeAs(source: any, type: any, services: Services): any {
        // Create the instance with Services as the first parameter if it's a DataModel
        const target = new type(services)
        for (const prop in source) {
            target[prop] = source[prop]
        }
        if (target.$deserialize != undefined) {
            target.$deserialize(source, services)
        }
        if (target.$setProperties != undefined) {
            target.$setProperties(target, services)
        }

        return target
    }

    /**
     * Performs a shallow copy of the object
     * @param source The source object to copy.
     */
    public copy<T>(source: any): T {
        return Object.assign({}, source)
    }

    /**
     * Debouncing is a form of rate-limiting: it prevents rapid-firing of events. You can use this to
     * throttle calls to an autocomplete API: call a function multiple times and it won't get called
     * more than once during the time interval you specify.
     */
    public debounce(waitInterval: number, action: () => void): () => void {
        let args: any | null, context: this | null, result
        let handle: number | any

        // Execute the callback function
        const execute = (): any => {
            result = action.apply(context || this, args || [])
            context = args = null
        }

        // Cancel the timeout (for rescheduling afterwards).
        function cancel() {
            if (handle) {
                clearTimeout(handle)
                handle = null
            }
        }

        // This is the actual result of the debounce call. It is a
        // wrapper function which you can invoke multiple times and
        // which will only be called once every "wait" milliseconds.
        const wrapper = () => {
            context = this
            args = arguments
            cancel()
            handle = window.setTimeout(execute, waitInterval)
        }

        return wrapper
    }

    public getResponsiveBreakpoint(size: 'xs' | 'sm' | 'md' | 'lg' | 'xl') {
        // bootstrap responsive breakpoints
        const sizes = {
            xs: 0,
            sm: 576,
            md: 768,
            lg: 992,
            xl: 1200,
        }

        return sizes[size] ? sizes[size] : 0
    }

    /*Boostrap Breakpoints for reference*/

    /*$grid-breakpoints: (
	  // Extra small screen / phone
	  xs: 0,

	  // Small screen / phone
	  sm: 576px,

	  // Medium screen / tablet
	  md: 768px,

	  // Large screen / desktop
	  lg: 992px,

	  // Extra large screen / wide desktop
	  xl: 1200px
	);*/

    public isEquivalent(a: any, b: any) {
        // Create arrays of property names
        const aProps = Object.getOwnPropertyNames(a)
        const bProps = Object.getOwnPropertyNames(b)

        // If number of properties is different,
        // objects are not equivalent
        if (aProps.length != bProps.length) {
            return false
        }

        for (const propName of aProps) {
            // If values of same property are not equal,
            // objects are not equivalent
            if (a[propName] !== b[propName]) {
                return false
            }
        }

        // If we made it this far, objects
        // are considered equivalent
        return true
    }

    public colorLuminance(hex: string, lum: number): string {
        // validate hex string
        hex = String(hex).replace(/[^0-9a-f]/gi, '')
        if (hex.length < 6) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
        }
        lum = lum || 0

        // convert to decimal and change luminosity
        let rgb = '#',
            c,
            i
        for (i = 0; i < 3; i++) {
            c = parseInt(hex.substr(i * 2, 2), 16)
            c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16)
            rgb += ('00' + c).substr(c.length)
        }

        return rgb
    }
}
